import {
  ContextCollectionState,
  type ContextCollectionStatus,
  type ContextCollectionTimerState,
} from '@electron/shared/types';
import { observer } from 'mobx-react-lite';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { electronApis } from '@/features/electron-api';
import { logger } from '@/lib/logger';
import { userStore } from '@/stores/user.store';
import { listenWithAbortIPC } from '@/utils/abort';
import {
  AppCapability,
  inDesktop,
  inElectron,
  isCapable,
} from '@/utils/capabilities';

import { useContextConsentAnalytics } from './use-context-consent-analytics';
import { useFirstRunSummary } from './use-first-run-summary';

export { ContextCollectionState };
export type { ContextCollectionStatus, ContextCollectionTimerState };

interface ContextCollectionContextValue {
  status: ContextCollectionStatus;
  timerState: ContextCollectionTimerState | undefined;
  accessibilityPermissions: boolean | null;
  supportsContextCollection: boolean;
  isStarting: boolean;
  isStopping: boolean;
  isLoading: boolean;
  startContextCollection: () => Promise<void>;
  stopContextCollection: () => Promise<void>;
  requestAccessibilityPermissions: () => Promise<boolean>;
  refreshAccessibilityPermissions: () => Promise<void>;
  forceStatusCheck: () => void;
  pauseContextCollection: (minutes: number) => Promise<void>;
  disableContextCollection: () => Promise<void>;
  resumeContextCollection: () => Promise<void>;
}

const ContextCollectionContext = createContext<
  ContextCollectionContextValue | undefined
>(undefined);

const isContextCollectionStartSettled = (
  state: ContextCollectionState
): boolean =>
  state !== ContextCollectionState.LOADING &&
  state !== ContextCollectionState.SPAWNING;

export const ContextCollectionProvider = observer(
  function ContextCollectionProvider({
    children,
  }: {
    children: React.ReactNode;
  }) {
    const user = userStore.user;

    const [status, setStatus] = useState<ContextCollectionStatus>({
      state: 'loading' as ContextCollectionState,
      isRunning: false,
      lastChecked: new Date(),
    });

    const [accessibilityPermissions, setAccessibilityPermissions] = useState<
      boolean | null
    >(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const liveStatusReceivedRef = useRef(false);

    const supportsContextCollection = useMemo(
      () => isCapable(AppCapability.ContextCapture),
      []
    );

    const refreshAccessibilityPermissions = useCallback(async () => {
      if (!supportsContextCollection) return;

      try {
        const hasPermissions =
          await electronApis.context.checkAccessibilityPermissions();
        setAccessibilityPermissions(hasPermissions);
      } catch (error) {
        console.error('Error checking accessibility permissions:', error);
        setAccessibilityPermissions(false);
      }
    }, [supportsContextCollection]);

    useEffect(() => {
      refreshAccessibilityPermissions();
      window.addEventListener('focus', refreshAccessibilityPermissions);
      return () =>
        window.removeEventListener('focus', refreshAccessibilityPermissions);
    }, [refreshAccessibilityPermissions]);

    useEffect(() => {
      if (!inElectron()) return;

      const controller = new AbortController();
      let active = true;

      listenWithAbortIPC(
        'context-collection-status-changed',
        (newStatus: ContextCollectionStatus) => {
          liveStatusReceivedRef.current = true;
          setStatus(newStatus);

          if (isContextCollectionStartSettled(newStatus.state)) {
            setIsStarting(false);
            setIsStopping(false);
          }
        },
        controller.signal
      );

      electronApis.context
        .getContextCollectionStatus()
        .then((initialStatus: ContextCollectionStatus) => {
          // Seed the displayed status from the mount snapshot, but only while no
          // live status event has arrived yet. A live event supersedes it;
          // applying the stale snapshot afterwards would bounce the status.
          if (active && initialStatus && !liveStatusReceivedRef.current) {
            setStatus(initialStatus);
          }
        })
        .catch((error) => {
          console.error(
            'Error getting initial context collection status:',
            error
          );
        });

      return () => {
        active = false;
        controller.abort();
      };
    }, []);

    const startContextCollection = useCallback(async () => {
      if (!supportsContextCollection) {
        logger.debug('Context collection not supported on this platform');
        return;
      }

      // Skip when collection is already starting, running, or intentionally
      // paused — the main process is authoritative and dedupes the observer
      // start; resuming a pause goes through resumeContextCollection, not here.
      if (
        status.state === ContextCollectionState.LOADING ||
        status.state === ContextCollectionState.SPAWNING ||
        status.state === ContextCollectionState.RUNNING ||
        status.state === ContextCollectionState.PAUSED ||
        status.state === ContextCollectionState.DISABLED ||
        status.state === ContextCollectionState.STOPPING ||
        status.isRunning
      ) {
        return;
      }

      if (!user?.email) {
        throw new Error('User email required');
      }

      setIsStarting(true);

      try {
        const hasPermissions =
          await electronApis.context.checkAccessibilityPermissions();
        setAccessibilityPermissions(hasPermissions);

        if (!hasPermissions) {
          throw new Error('Accessibility permissions required');
        }

        await electronApis.context.startContextCollection(
          user.email,
          user.id,
          userStore.hasInternalAccess
        );
      } catch (error) {
        setIsStarting(false);
        throw error;
      }
    }, [
      status.state,
      status.isRunning,
      supportsContextCollection,
      user?.email,
      user?.id,
    ]);

    const stopContextCollection = useCallback(async () => {
      if (!supportsContextCollection) {
        throw new Error('Context collection not supported on this platform');
      }

      setIsStopping(true);
      try {
        await electronApis.context.stopContextCollection();
      } catch (error) {
        setIsStopping(false);
        throw error;
      }
    }, [supportsContextCollection]);

    const requestAccessibilityPermissions = useCallback(async () => {
      if (!supportsContextCollection) {
        throw new Error('Context collection not supported on this platform');
      }

      try {
        const granted =
          await electronApis.context.requestAccessibilityPermissions();
        setAccessibilityPermissions(granted);
        return granted;
      } catch (error) {
        console.error('Error requesting accessibility permissions:', error);
        return false;
      }
    }, [supportsContextCollection]);

    const forceStatusCheck = useCallback(() => {
      if (!inDesktop()) return;
      electronApis.context
        .forceContextCollectionStatusCheck()
        .catch((error) => {
          console.error('Error forcing status check:', error);
        });
    }, []);

    const pauseContextCollection = useCallback(
      async (minutes: number): Promise<void> => {
        await electronApis.context.pauseContextCollection(minutes);
      },
      []
    );

    const disableContextCollection = useCallback(async (): Promise<void> => {
      await electronApis.context.disableContextCollection();
    }, []);

    const resumeContextCollection = useCallback(async (): Promise<void> => {
      const hasPermissions =
        await electronApis.context.checkAccessibilityPermissions();
      setAccessibilityPermissions(hasPermissions);

      if (!hasPermissions) {
        throw new Error('Accessibility permissions required');
      }

      await electronApis.context.resumeContextCollection();
    }, []);

    // Post-onboarding first-run summary lives here so its store reads stay
    // reactive under this observer and it mounts above the router subtree.
    useFirstRunSummary(status.isRunning);
    useContextConsentAnalytics(user?.id, status.consent);

    const value = useMemo(
      () => ({
        status,
        timerState: status.timerState,
        accessibilityPermissions,
        supportsContextCollection,
        isStarting,
        isStopping,
        isLoading: isStarting || isStopping || status.state === 'loading',
        startContextCollection,
        stopContextCollection,
        requestAccessibilityPermissions,
        refreshAccessibilityPermissions,
        forceStatusCheck,
        pauseContextCollection,
        disableContextCollection,
        resumeContextCollection,
      }),
      [
        status,
        accessibilityPermissions,
        supportsContextCollection,
        isStarting,
        isStopping,
        startContextCollection,
        stopContextCollection,
        requestAccessibilityPermissions,
        refreshAccessibilityPermissions,
        forceStatusCheck,
        pauseContextCollection,
        disableContextCollection,
        resumeContextCollection,
      ]
    );

    return (
      <ContextCollectionContext.Provider value={value}>
        {children}
      </ContextCollectionContext.Provider>
    );
  }
);

export const useElectronContextCollection = () => {
  const context = useContext(ContextCollectionContext);
  if (context === undefined) {
    throw new Error(
      'useElectronContextCollection must be used within a ContextCollectionProvider'
    );
  }
  return context;
};
